import React, { useState, useRef, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react';
import { ProcessingResult } from '../../types/client';
import { Country } from '../../types';
import { ClientImageProcessor } from '../../services/client/imageProcessor';
import { debug } from '../../utils/debug';
import { useWizard } from '../wizard/WizardContext';
import Icon from '../common/Icon';
import { ICONS } from '../../utils/fontAwesome';

// Utility function to detect touch devices
const isTouchDevice = () => {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
};

// Configurable guide line system - 6 lines INSIDE the selection box (4H + 2V)
interface GuideLineConfig {
  horizontal: number[]; // Relative positions within selection (0.0 to 1.0) - 4 lines
  vertical: number[];   // Relative positions within selection (0.0 to 1.0) - 2 lines  
  dimensions: {
    width: number;  // Reference dimensions for calculations
    height: number;
  };
}

const GUIDE_CONFIGS: Record<string, GuideLineConfig> = {
  FI: { // Finland - CORRECTED exact positions matching req1.png diagram measurements
    horizontal: [
      0.08575,  // Line 1: 56px from top (4mm top margin)
      0.12863,  // Line 2: 84px from top (56px + 28px gap)
      0.81012,  // Line 3: 529px from top (56px + 28px + 445px middle area)
      0.85299   // Line 4: 557px from top (56px + 28px + 445px + 28px gap)
    ],
    vertical: [
      0.458, // Left center tolerance line - 229px from left (250px - 21px)
      0.542  // Right center tolerance line - 271px from left (250px + 21px)
    ],
    dimensions: { width: 500, height: 653 }
  },
  US: { // USA - using Finland's vertical guide requirements
    horizontal: [0.15, 0.25, 0.75, 0.85],
    vertical: [
      0.458, // Left center tolerance line - matching Finland's requirements
      0.542  // Right center tolerance line - matching Finland's requirements
    ],
    dimensions: { width: 600, height: 600 }
  },
  UK: { // United Kingdom - using Finland's vertical guide requirements
    horizontal: [0.15, 0.25, 0.75, 0.85],
    vertical: [
      0.458, // Left center tolerance line - matching Finland's requirements
      0.542  // Right center tolerance line - matching Finland's requirements
    ],
    dimensions: { width: 600, height: 750 }
  },
  AU: { // Australia - using Finland's vertical guide requirements
    horizontal: [0.15, 0.25, 0.75, 0.85],
    vertical: [
      0.458, // Left center tolerance line - matching Finland's requirements
      0.542  // Right center tolerance line - matching Finland's requirements
    ],
    dimensions: { width: 1200, height: 1600 }
  },
  IE: { // Ireland - using Finland's vertical guide requirements
    horizontal: [0.15, 0.25, 0.75, 0.85],
    vertical: [
      0.458, // Left center tolerance line - matching Finland's requirements
      0.542  // Right center tolerance line - matching Finland's requirements
    ],
    dimensions: { width: 715, height: 951 }
  },
  GENERIC: { // Generic fallback - using Finland's vertical guide requirements
    horizontal: [0.15, 0.25, 0.75, 0.85],
    vertical: [
      0.458, // Left center tolerance line - matching Finland's requirements
      0.542  // Right center tolerance line - matching Finland's requirements
    ],
    dimensions: { width: 500, height: 650 }
  }
};

interface ManualSelectionEditorProps {
  result: ProcessingResult;
  country: Country;
  processor: ClientImageProcessor;
  onFinalResult: (blob: Blob) => void;
  onProcessingChange?: (processing: boolean) => void;
}

export interface ManualSelectionEditorRef {
  generatePhoto: () => Promise<void>;
}

interface SelectionArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

const ManualSelectionEditor = React.memo(forwardRef<ManualSelectionEditorRef, ManualSelectionEditorProps>(({
  result,
  country,
  processor,
  onFinalResult,
  onProcessingChange
}, ref) => {
  // const { t } = useTranslation(); // Removed as it's not used in this component anymore
  const { 
    state, 
    setSelectionState, 
    setBackgroundRemoving, 
    setBackgroundRemoved, 
    setProcessedResult 
  } = useWizard();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [selection, setSelection] = useState<SelectionArea>(
    state.selectionState || {
      x: 50,
      y: 50,
      width: 200,
      height: 250
    }
  );
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [resizeHandle, setResizeHandle] = useState<string | null>(null);
  const [canvasScale, setCanvasScale] = useState(1);
  const [drawOffset, setDrawOffset] = useState({ x: 0, y: 0 });
  const [drawScale, setDrawScale] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isLocalBackgroundRemoving, setIsLocalBackgroundRemoving] = useState(false);
  const [backgroundProgress, setBackgroundProgress] = useState(0);

  // Get guide configuration for current country
  const guideConfig = GUIDE_CONFIGS[country.code] || GUIDE_CONFIGS.GENERIC;
  
  // Combined background removing state (local takes priority for immediate feedback)
  const isBackgroundRemoving = isLocalBackgroundRemoving || state.isBackgroundRemoving;

  // Calculate target aspect ratio based on country requirements
  const targetAspectRatio = country.photo_width / country.photo_height;

  useEffect(() => {
    drawCanvas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selection, result.backgroundRemoved]);

  // Save selection state to wizard context with debouncing and skip during dragging to avoid performance issues
  useEffect(() => {
    // Skip saving during active dragging/resizing for better performance
    if (isDragging || isResizing) {
      return;
    }

    const timeoutId = setTimeout(() => {
      setSelectionState(selection);
    }, 150); // Increased debounce to 150ms for better performance

    return () => clearTimeout(timeoutId);
  }, [selection, setSelectionState, isDragging, isResizing]);

  useEffect(() => {
    // Only initialize selection if we don't have a saved selection state
    if (state.selectionState) {
      return; // Use the saved selection from wizard state
    }

    // Initialize selection based on image and country requirements
    const imageWidth = result.backgroundRemoved.width;
    const imageHeight = result.backgroundRemoved.height;
    
    // Calculate optimal initial selection - make it much larger and more usable
    let selectionWidth, selectionHeight;
    
    if (imageWidth / imageHeight > targetAspectRatio) {
      // Image is wider than target ratio - use more of the height
      selectionHeight = Math.min(imageHeight * 0.9, 600); // Increased from 0.8 to 0.9
      selectionWidth = selectionHeight * targetAspectRatio;
    } else {
      // Image is taller than target ratio - use more of the width
      selectionWidth = Math.min(imageWidth * 0.85, 500); // Increased from 0.8 to 0.85
      selectionHeight = selectionWidth / targetAspectRatio;
    }

    // Ensure minimum usable size - much larger than before
    const minWidth = Math.min(imageWidth * 0.6, 300);
    const minHeight = Math.min(imageHeight * 0.7, 400);
    
    selectionWidth = Math.max(selectionWidth, minWidth);
    selectionHeight = Math.max(selectionHeight, minHeight);
    
    // Maintain aspect ratio after applying minimums
    if (selectionWidth / selectionHeight > targetAspectRatio) {
      selectionWidth = selectionHeight * targetAspectRatio;
    } else {
      selectionHeight = selectionWidth / targetAspectRatio;
    }

    const initialSelection = {
      x: (imageWidth - selectionWidth) / 2,
      y: Math.max(0, Math.min(imageHeight * 0.05, (imageHeight - selectionHeight) / 3)), // Position in upper third
      width: selectionWidth,
      height: selectionHeight
    };

    setSelection(initialSelection);
  }, [result.backgroundRemoved, targetAspectRatio, state.selectionState]);

  const drawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !result.backgroundRemoved) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Calculate display size - make canvas larger for better usability
    const maxWidth = 1000;
    const maxHeight = 750;
    const imageWidth = result.backgroundRemoved.width;
    const imageHeight = result.backgroundRemoved.height;
    const imageAspectRatio = imageWidth / imageHeight;
    const maxAspectRatio = maxWidth / maxHeight;

    let displayWidth, displayHeight;

    // More robust sizing logic to prevent stretching
    if (imageAspectRatio > maxAspectRatio) {
      // Image is wider relative to our max dimensions - fit to width
      displayWidth = maxWidth;
      displayHeight = maxWidth / imageAspectRatio;
    } else {
      // Image is taller relative to our max dimensions - fit to height  
      displayHeight = maxHeight;
      displayWidth = maxHeight * imageAspectRatio;
    }

    // Ensure minimum usable size while maintaining aspect ratio
    const minDisplaySize = 300;
    if (displayWidth < minDisplaySize || displayHeight < minDisplaySize) {
      if (imageAspectRatio > 1) {
        // Landscape - scale based on width
        displayWidth = Math.max(minDisplaySize, displayWidth);
        displayHeight = displayWidth / imageAspectRatio;
      } else {
        // Portrait - scale based on height
        displayHeight = Math.max(minDisplaySize, displayHeight);
        displayWidth = displayHeight * imageAspectRatio;
      }
    }

    // Round to avoid sub-pixel issues
    displayWidth = Math.round(displayWidth);
    displayHeight = Math.round(displayHeight);

    // Set canvas internal dimensions
    canvas.width = displayWidth;
    canvas.height = displayHeight;

    // Store the scale factor for later use (will be recalculated in draw function)
    const scale = displayWidth / result.backgroundRemoved.width;
    setCanvasScale(scale);

    debug.log('🔍 Canvas Sizing Debug:');
    debug.log('  - Original image:', imageWidth, 'x', imageHeight);
    debug.log('  - Image aspect ratio:', imageAspectRatio.toFixed(3));
    debug.log('  - Max container:', maxWidth, 'x', maxHeight);
    debug.log('  - Max aspect ratio:', maxAspectRatio.toFixed(3));
    debug.log('  - Calculated canvas size:', displayWidth, 'x', displayHeight);
    debug.log('  - Final aspect ratio:', (displayWidth / displayHeight).toFixed(3));
    debug.log('  - Scale factor:', scale);
    debug.log('  - Selection (original):', selection);
    debug.log('  - Selection (scaled):', {
      x: selection.x * scale,
      y: selection.y * scale, 
      width: selection.width * scale,
      height: selection.height * scale
    });

    // Create image from ImageData
    const tempCanvas = document.createElement('canvas');
    const tempCtx = tempCanvas.getContext('2d')!;
    tempCanvas.width = result.backgroundRemoved.width;
    tempCanvas.height = result.backgroundRemoved.height;
    tempCtx.putImageData(result.backgroundRemoved, 0, 0);

    // Clear and draw background image - maintain aspect ratio
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Calculate proper scaling to fit image while maintaining aspect ratio
    const srcWidth = result.backgroundRemoved.width;
    const srcHeight = result.backgroundRemoved.height;
    const canvasWidth = canvas.width;
    const canvasHeight = canvas.height;
    
    // Calculate scale to fit image in canvas while maintaining aspect ratio
    const scaleX = canvasWidth / srcWidth;
    const scaleY = canvasHeight / srcHeight;
    const drawScale = Math.min(scaleX, scaleY);
    
    // Calculate centered position
    const drawWidth = srcWidth * drawScale;
    const drawHeight = srcHeight * drawScale;
    const offsetX = (canvasWidth - drawWidth) / 2;
    const offsetY = (canvasHeight - drawHeight) / 2;
    
    // Store drawing parameters for coordinate conversion
    setDrawScale(drawScale);
    setDrawOffset({ x: offsetX, y: offsetY });
    
    debug.log('🎨 Drawing Debug:');
    debug.log('  - Image size:', srcWidth, 'x', srcHeight);
    debug.log('  - Canvas size:', canvasWidth, 'x', canvasHeight);
    debug.log('  - Draw scale:', drawScale);
    debug.log('  - Draw size:', drawWidth, 'x', drawHeight);
    debug.log('  - Offset:', offsetX, offsetY);
    
    ctx.drawImage(tempCanvas, offsetX, offsetY, drawWidth, drawHeight);

    // Draw semi-transparent overlay
    ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Clear selection area to show original image - account for offset and proper scaling
    const scaledSelection = {
      x: offsetX + (selection.x * drawScale),
      y: offsetY + (selection.y * drawScale),
      width: selection.width * drawScale,
      height: selection.height * drawScale,
    };

    debug.log('🎯 Selection Debug:');
    debug.log('  - Original selection:', selection);
    debug.log('  - Scaled selection:', scaledSelection);
    debug.log('  - Draw offset:', offsetX, offsetY);
    debug.log('  - Draw scale:', drawScale);

    ctx.clearRect(scaledSelection.x, scaledSelection.y, scaledSelection.width, scaledSelection.height);
    ctx.drawImage(
      tempCanvas,
      selection.x, selection.y, selection.width, selection.height,
      scaledSelection.x, scaledSelection.y, scaledSelection.width, scaledSelection.height
    );

    // Draw selection border - make it more prominent
    ctx.strokeStyle = '#3b82f6';
    ctx.lineWidth = 3; // Increased from 2 to 3
    ctx.setLineDash([8, 4]); // Changed dash pattern for better visibility
    ctx.strokeRect(scaledSelection.x, scaledSelection.y, scaledSelection.width, scaledSelection.height);
    
    // Add inner bright border for contrast
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 1;
    ctx.setLineDash([]);
    ctx.strokeRect(scaledSelection.x + 1, scaledSelection.y + 1, scaledSelection.width - 2, scaledSelection.height - 2);

    // Draw 6 configurable guide lines INSIDE the selection box (4H + 2V)
    ctx.setLineDash([]);
    ctx.strokeStyle = '#ef4444'; // Red color for guide lines
    ctx.lineWidth = 2;
    
    // Draw 4 horizontal lines inside the selection box (they move with the selection)
    guideConfig.horizontal.forEach(relativeY => {
      const lineY = scaledSelection.y + scaledSelection.height * relativeY;
      ctx.beginPath();
      ctx.moveTo(scaledSelection.x, lineY);
      ctx.lineTo(scaledSelection.x + scaledSelection.width, lineY);
      ctx.stroke();
    });
    
    // Draw 2 vertical lines inside the selection box (head width boundaries)
    guideConfig.vertical.forEach(relativeX => {
      const lineX = scaledSelection.x + scaledSelection.width * relativeX;
      ctx.beginPath();
      ctx.moveTo(lineX, scaledSelection.y);
      ctx.lineTo(lineX, scaledSelection.y + scaledSelection.height);
      ctx.stroke();
    });

    // Draw resize handles - standard size, works well for both desktop and mobile
    const handleSize = 24;
    
    const handles = [
      { x: scaledSelection.x - handleSize / 2, y: scaledSelection.y - handleSize / 2, cursor: 'nw-resize' },
      { x: scaledSelection.x + scaledSelection.width - handleSize / 2, y: scaledSelection.y - handleSize / 2, cursor: 'ne-resize' },
      { x: scaledSelection.x - handleSize / 2, y: scaledSelection.y + scaledSelection.height - handleSize / 2, cursor: 'sw-resize' },
      { x: scaledSelection.x + scaledSelection.width - handleSize / 2, y: scaledSelection.y + scaledSelection.height - handleSize / 2, cursor: 'se-resize' },
    ];

    handles.forEach(handle => {
      // Draw handle with shadow for depth
      // Shadow
      ctx.fillStyle = 'rgba(0, 0, 0, 0.2)';
      ctx.fillRect(handle.x + 2, handle.y + 2, handleSize, handleSize);
      
      // Main handle
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(handle.x, handle.y, handleSize, handleSize);
      
      // Blue border
      ctx.strokeStyle = '#3b82f6';
      ctx.lineWidth = 3;
      ctx.strokeRect(handle.x, handle.y, handleSize, handleSize);
      
      // Inner grip pattern - adjust for handle size
      ctx.fillStyle = '#3b82f6';
      const gripSize = isTouchDevice() ? 4 : 3;
      const gripSpacing = isTouchDevice() ? 8 : 6;
      const gripOffset = isTouchDevice() ? 8 : 6;
      for (let i = 0; i < 2; i++) {
        for (let j = 0; j < 2; j++) {
          ctx.fillRect(
            handle.x + gripOffset + i * gripSpacing, 
            handle.y + gripOffset + j * gripSpacing, 
            gripSize, gripSize
          );
        }
      }
    });
  }, [selection, result.backgroundRemoved, guideConfig]);

  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas || isBackgroundRemoving) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Convert mouse coordinates to canvas coordinates (accounting for CSS scaling)
    const canvasX = (x / rect.width) * canvas.width;
    const canvasY = (y / rect.height) * canvas.height;

    debug.log('🖱️ Mouse Down Debug:');
    debug.log('  - Raw mouse:', x, y);
    debug.log('  - Canvas rect:', rect.width, 'x', rect.height);
    debug.log('  - Canvas internal:', canvas.width, 'x', canvas.height);
    debug.log('  - Canvas coordinates:', canvasX, canvasY);
    debug.log('  - Draw offset:', drawOffset);
    debug.log('  - Draw scale:', drawScale);

    const scaledSelection = {
      x: drawOffset.x + (selection.x * drawScale),
      y: drawOffset.y + (selection.y * drawScale),
      width: selection.width * drawScale,
      height: selection.height * drawScale,
    };

    debug.log('  - Scaled selection:', scaledSelection);

    // Check resize handles - using same larger size, extra large on touch devices
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    const handleSize = isTouchDevice ? 36 : 24; // Match drawing handle size
    const handles = [
      { x: scaledSelection.x - handleSize / 2, y: scaledSelection.y - handleSize / 2, type: 'nw' },
      { x: scaledSelection.x + scaledSelection.width - handleSize / 2, y: scaledSelection.y - handleSize / 2, type: 'ne' },
      { x: scaledSelection.x - handleSize / 2, y: scaledSelection.y + scaledSelection.height - handleSize / 2, type: 'sw' },
      { x: scaledSelection.x + scaledSelection.width - handleSize / 2, y: scaledSelection.y + scaledSelection.height - handleSize / 2, type: 'se' },
    ];

    debug.log('  - Handle positions:', handles);

    for (const handle of handles) {
      if (canvasX >= handle.x && canvasX <= handle.x + handleSize && canvasY >= handle.y && canvasY <= handle.y + handleSize) {
        debug.log('  - ✅ Handle clicked:', handle.type);
        setIsResizing(true);
        setResizeHandle(handle.type);
        setDragStart({ x: canvasX, y: canvasY });
        return;
      }
    }

    // Check if clicking inside selection for dragging
    if (canvasX >= scaledSelection.x && canvasX <= scaledSelection.x + scaledSelection.width &&
        canvasY >= scaledSelection.y && canvasY <= scaledSelection.y + scaledSelection.height) {
      debug.log('  - ✅ Selection area clicked for dragging');
      setIsDragging(true);
      setDragStart({ x: canvasX, y: canvasY });
    }
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas || isBackgroundRemoving) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Convert mouse coordinates to canvas coordinates (same as mouse down)
    const canvasX = (x / rect.width) * canvas.width;
    const canvasY = (y / rect.height) * canvas.height;

    if (isDragging) {
      const deltaX = (canvasX - dragStart.x) / drawScale;
      const deltaY = (canvasY - dragStart.y) / drawScale;

      const newSelection = {
        ...selection,
        x: Math.max(0, Math.min(result.backgroundRemoved.width - selection.width, selection.x + deltaX)),
        y: Math.max(0, Math.min(result.backgroundRemoved.height - selection.height, selection.y + deltaY)),
      };

      setSelection(newSelection);
      setDragStart({ x: canvasX, y: canvasY });
    } else if (isResizing && resizeHandle) {
      const deltaX = (canvasX - dragStart.x) / drawScale;
      const deltaY = (canvasY - dragStart.y) / drawScale;

      let newSelection = { ...selection };

      switch (resizeHandle) {
        case 'nw':
          newSelection.x += deltaX;
          newSelection.y += deltaY;
          newSelection.width -= deltaX;
          newSelection.height -= deltaY;
          break;
        case 'ne':
          newSelection.y += deltaY;
          newSelection.width += deltaX;
          newSelection.height -= deltaY;
          break;
        case 'sw':
          newSelection.x += deltaX;
          newSelection.width -= deltaX;
          newSelection.height += deltaY;
          break;
        case 'se':
          newSelection.width += deltaX;
          newSelection.height += deltaY;
          break;
      }

      // Maintain aspect ratio
      if (newSelection.width / newSelection.height > targetAspectRatio) {
        newSelection.width = newSelection.height * targetAspectRatio;
      } else {
        newSelection.height = newSelection.width / targetAspectRatio;
      }

      // Ensure selection stays within bounds with better minimum sizes
      const minWidth = Math.min(result.backgroundRemoved.width * 0.3, 200); // Larger minimum
      const minHeight = Math.min(result.backgroundRemoved.height * 0.4, 250); // Larger minimum
      
      newSelection.x = Math.max(0, Math.min(result.backgroundRemoved.width - newSelection.width, newSelection.x));
      newSelection.y = Math.max(0, Math.min(result.backgroundRemoved.height - newSelection.height, newSelection.y));
      newSelection.width = Math.max(minWidth, Math.min(result.backgroundRemoved.width - newSelection.x, newSelection.width));
      newSelection.height = Math.max(minHeight, Math.min(result.backgroundRemoved.height - newSelection.y, newSelection.height));

      setSelection(newSelection);
      setDragStart({ x: canvasX, y: canvasY });
    } else {
      // Update cursor based on hover position (only when not dragging/resizing)
      const scaledSelection = {
        x: drawOffset.x + (selection.x * drawScale),
        y: drawOffset.y + (selection.y * drawScale),
        width: selection.width * drawScale,
        height: selection.height * drawScale,
      };

      // Check resize handles for cursor feedback
      const handleSize = 24;
      const handles = [
        { x: scaledSelection.x - handleSize / 2, y: scaledSelection.y - handleSize / 2, cursor: 'nw-resize' },
        { x: scaledSelection.x + scaledSelection.width - handleSize / 2, y: scaledSelection.y - handleSize / 2, cursor: 'ne-resize' },
        { x: scaledSelection.x - handleSize / 2, y: scaledSelection.y + scaledSelection.height - handleSize / 2, cursor: 'sw-resize' },
        { x: scaledSelection.x + scaledSelection.width - handleSize / 2, y: scaledSelection.y + scaledSelection.height - handleSize / 2, cursor: 'se-resize' },
      ];

      let cursorStyle = 'default';

      for (const handle of handles) {
        if (canvasX >= handle.x && canvasX <= handle.x + handleSize && canvasY >= handle.y && canvasY <= handle.y + handleSize) {
          cursorStyle = handle.cursor;
          break;
        }
      }

      // Check if hovering over selection area for move cursor
      if (cursorStyle === 'default' && 
          canvasX >= scaledSelection.x && canvasX <= scaledSelection.x + scaledSelection.width &&
          canvasY >= scaledSelection.y && canvasY <= scaledSelection.y + scaledSelection.height) {
        cursorStyle = 'move';
      }

      canvas.style.cursor = cursorStyle;
    }
  };


  const handleMouseUp = () => {
    setIsDragging(false);
    setIsResizing(false);
    setResizeHandle(null);
  };

  // Touch event handlers for mobile support
  const handleTouchStart = (e: React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault(); // Prevent scrolling
    const canvas = canvasRef.current;
    if (!canvas || e.touches.length === 0 || isBackgroundRemoving) return;

    const touch = e.touches[0];
    const rect = canvas.getBoundingClientRect();
    const x = touch.clientX - rect.left;
    const y = touch.clientY - rect.top;

    // Convert touch coordinates to canvas coordinates (same logic as mouse)
    const canvasX = (x / rect.width) * canvas.width;
    const canvasY = (y / rect.height) * canvas.height;

    const scaledSelection = {
      x: drawOffset.x + (selection.x * drawScale),
      y: drawOffset.y + (selection.y * drawScale),
      width: selection.width * drawScale,
      height: selection.height * drawScale,
    };

    // Check resize handles - match drawing handle size
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    const handleSize = isTouchDevice ? 36 : 24;
    const handles = [
      { x: scaledSelection.x - handleSize / 2, y: scaledSelection.y - handleSize / 2, type: 'nw' },
      { x: scaledSelection.x + scaledSelection.width - handleSize / 2, y: scaledSelection.y - handleSize / 2, type: 'ne' },
      { x: scaledSelection.x - handleSize / 2, y: scaledSelection.y + scaledSelection.height - handleSize / 2, type: 'sw' },
      { x: scaledSelection.x + scaledSelection.width - handleSize / 2, y: scaledSelection.y + scaledSelection.height - handleSize / 2, type: 'se' },
    ];

    for (const handle of handles) {
      if (canvasX >= handle.x && canvasX <= handle.x + handleSize && canvasY >= handle.y && canvasY <= handle.y + handleSize) {
        setIsResizing(true);
        setResizeHandle(handle.type);
        setDragStart({ x: canvasX, y: canvasY });
        return;
      }
    }

    // Check if touching inside selection for dragging
    if (canvasX >= scaledSelection.x && canvasX <= scaledSelection.x + scaledSelection.width &&
        canvasY >= scaledSelection.y && canvasY <= scaledSelection.y + scaledSelection.height) {
      setIsDragging(true);
      setDragStart({ x: canvasX, y: canvasY });
    }
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault(); // Prevent scrolling
    const canvas = canvasRef.current;
    if (!canvas || e.touches.length === 0 || isBackgroundRemoving) return;

    const touch = e.touches[0];
    const rect = canvas.getBoundingClientRect();
    const x = touch.clientX - rect.left;
    const y = touch.clientY - rect.top;

    // Convert touch coordinates to canvas coordinates
    const canvasX = (x / rect.width) * canvas.width;
    const canvasY = (y / rect.height) * canvas.height;

    if (isDragging) {
      const deltaX = canvasX - dragStart.x;
      const deltaY = canvasY - dragStart.y;

      // Convert deltas back to image coordinates
      const imageDeltaX = deltaX / drawScale;
      const imageDeltaY = deltaY / drawScale;

      const newX = Math.max(0, Math.min(result.backgroundRemoved.width - selection.width, selection.x + imageDeltaX));
      const newY = Math.max(0, Math.min(result.backgroundRemoved.height - selection.height, selection.y + imageDeltaY));

      setSelection(prev => ({
        ...prev,
        x: newX,
        y: newY
      }));

      setDragStart({ x: canvasX, y: canvasY });
    } else if (isResizing && resizeHandle) {
      const deltaX = canvasX - dragStart.x;
      const deltaY = canvasY - dragStart.y;

      // Convert deltas to image coordinates
      const imageDeltaX = deltaX / drawScale;
      const imageDeltaY = deltaY / drawScale;

      let newSelection = { ...selection };

      switch (resizeHandle) {
        case 'nw':
          newSelection.x = Math.max(0, selection.x + imageDeltaX);
          newSelection.y = Math.max(0, selection.y + imageDeltaY);
          newSelection.width = selection.width - imageDeltaX;
          newSelection.height = selection.height - imageDeltaY;
          break;
        case 'ne':
          newSelection.y = Math.max(0, selection.y + imageDeltaY);
          newSelection.width = selection.width + imageDeltaX;
          newSelection.height = selection.height - imageDeltaY;
          break;
        case 'sw':
          newSelection.x = Math.max(0, selection.x + imageDeltaX);
          newSelection.width = selection.width - imageDeltaX;
          newSelection.height = selection.height + imageDeltaY;
          break;
        case 'se':
          newSelection.width = selection.width + imageDeltaX;
          newSelection.height = selection.height + imageDeltaY;
          break;
      }

      // Enforce minimum size and bounds
      const minSize = 50;
      newSelection.width = Math.max(minSize, Math.min(result.backgroundRemoved.width - newSelection.x, newSelection.width));
      newSelection.height = Math.max(minSize, Math.min(result.backgroundRemoved.height - newSelection.y, newSelection.height));

      // Maintain aspect ratio
      const currentRatio = newSelection.width / newSelection.height;
      if (Math.abs(currentRatio - targetAspectRatio) > 0.01) {
        if (currentRatio > targetAspectRatio) {
          newSelection.width = newSelection.height * targetAspectRatio;
        } else {
          newSelection.height = newSelection.width / targetAspectRatio;
        }
      }

      setSelection(newSelection);
      setDragStart({ x: canvasX, y: canvasY });
    }
  };

  const handleTouchEnd = (e: React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    setIsDragging(false);
    setIsResizing(false);
    setResizeHandle(null);
  };

  const handleBackgroundRemoval = async () => {
    // Set local loading state immediately for instant spinner feedback
    setIsLocalBackgroundRemoving(true);
    setBackgroundRemoving(true);
    
    try {
      debug.log('🎨 Starting background removal from ManualSelectionEditor...');
      
      // Get the original file from result.processedImage
      const originalFile = result.processedImage as File;
      
      // Remove background using processor with progress callback
      const backgroundRemovedImageData = await processor.removeBackgroundFromFile(
        originalFile,
        (progress) => setBackgroundProgress(progress)
      );
      
      // Update the result with the new background-removed image
      const updatedResult = {
        ...result,
        backgroundRemoved: backgroundRemovedImageData
      };
      
      setProcessedResult(updatedResult);
      setBackgroundRemoved(true);
      debug.log('✓ Background removal completed and state updated');
      
    } catch (error) {
      debug.error('❌ Background removal failed:', error);
      alert('Background removal failed. Please try again.');
    } finally {
      setIsLocalBackgroundRemoving(false);
      setBackgroundRemoving(false);
      setBackgroundProgress(0);
    }
  };

  const handleGenerate = async () => {
    setIsProcessing(true);
    onProcessingChange?.(true);
    try {
      debug.log('🎯 Manual selection coordinates being sent:');
      debug.log('  - Selection:', selection);
      debug.log('  - Image dimensions:', result.backgroundRemoved.width, 'x', result.backgroundRemoved.height);
      debug.log('  - Canvas scale:', canvasScale);
      debug.log('  - Target country:', country.name, `(${country.photo_width}x${country.photo_height})`);
      
      const finalBlob = await processor.processWithManualSelection(
        result.backgroundRemoved,
        selection,
        country
      );
      onFinalResult(finalBlob);
    } catch (error) {
      debug.error('Failed to generate passport photo:', error);
      alert('Failed to generate passport photo. Please try again.');
    } finally {
      setIsProcessing(false);
      onProcessingChange?.(false);
    }
  };

  useImperativeHandle(ref, () => ({
    generatePhoto: handleGenerate
  }), [handleGenerate]);

  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
          <div className="flex flex-col lg:flex-row gap-6">
            <div className="flex-1 relative">
              <div className="border-2 border-blue-300 dark:border-blue-600 rounded-lg overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800 flex justify-center items-center shadow-inner" 
                   style={{ minHeight: '400px' }}>
                <canvas
                  ref={canvasRef}
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseUp}
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                  onTouchCancel={handleTouchEnd}
                  className={`shadow-sm touch-none ${isBackgroundRemoving ? 'cursor-wait' : 'cursor-crosshair'}`}
                  style={{ 
                    maxWidth: '100%',
                    maxHeight: '100%',
                    width: 'auto',
                    height: 'auto',
                    display: 'block',
                    opacity: isBackgroundRemoving ? 0.6 : 1
                  }}
                />
              </div>
              {/* Readonly overlay during background removal */}
              {isBackgroundRemoving && (
                <div className="absolute inset-0 bg-transparent cursor-wait rounded-lg" />
              )}
            </div>

            <div className="lg:w-80 bg-white dark:bg-gray-800 text-gray-900 dark:text-white p-6 space-y-4 border border-gray-200 dark:border-gray-600 rounded-lg shadow-sm">
              {/* Control Panel Header */}
              <div className="border-b border-gray-200 pb-4 mb-6">
                <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-1">
                  <Icon icon={ICONS.SLIDERS} className="mr-2" />
                  Control Panel
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Adjust your passport photo selection
                </p>
              </div>

              {/* Background Removal Section */}
              {!state.isBackgroundRemoved && (
                <div className="mb-6">
                  <button
                    onClick={handleBackgroundRemoval}
                    disabled={isBackgroundRemoving}
                    className="w-full py-3 px-4 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 text-white font-semibold rounded-lg transition-colors shadow-md"
                  >
                    {isBackgroundRemoving ? (
                      <span className="flex items-center justify-center">
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        {backgroundProgress > 0 ? `Removing background... ${Math.round(backgroundProgress)}%` : 'Removing background...'}
                      </span>
                    ) : (
                      '🎨 Remove Background'
                    )}
                  </button>
                </div>
              )}


              {/* Primary Action Button */}
              <button
                onClick={handleGenerate}
                disabled={isProcessing || isBackgroundRemoving}
                className="w-full py-4 px-6 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white font-semibold rounded-lg transition-colors mb-6 shadow-lg"
              >
                {isProcessing ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Generating...
                  </span>
                ) : (
                  <>
                    <Icon icon={ICONS.SAVE} className="mr-2" />
                    Make photo
                  </>
                )}
              </button>

              {/* Tips and Instructions */}
              <div className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center">
                  <Icon icon={ICONS.LIGHTBULB} className="mr-2" />
                  Tips
                </h3>
                <div className="text-sm text-gray-600 dark:text-gray-300 space-y-2">
                  <div className="flex items-start space-x-2">
                    <Icon icon={ICONS.CIRCLE} className="text-blue-600 text-xs mt-1" />
                    <span>Drag inside rectangle to move</span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <Icon icon={ICONS.CIRCLE} className="text-blue-600 text-xs mt-1" />
                    <span>Drag corners to resize</span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <Icon icon={ICONS.CIRCLE} className="text-blue-600 text-xs mt-1" />
                    <span>Aspect ratio is maintained automatically</span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <Icon icon={ICONS.CIRCLE} className="text-blue-600 text-xs mt-1" />
                    <span>Face should be in the upper portion</span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <Icon icon={ICONS.CIRCLE} className="text-red-600 text-xs mt-1" />
                    <span>6 red lines inside selection show {country.name} guides</span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <Icon icon={ICONS.CIRCLE} className="text-red-600 text-xs mt-1" />
                    <span>4 horizontal + 2 vertical lines move with selection</span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <Icon icon={ICONS.CIRCLE} className="text-red-600 text-xs mt-1" />
                    <span>Horizontal: head position margins</span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <Icon icon={ICONS.CIRCLE} className="text-red-600 text-xs mt-1" />
                    <span>Vertical: center tolerance zone (±21px/1.5mm)</span>
                  </div>
                </div>
              </div>

              {/* Country Info */}
              <div className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center">
                  <Icon icon={ICONS.GLOBE} className="mr-2" />
                  Requirements
                </h3>
                <div className="text-sm text-gray-600 dark:text-gray-300 space-y-2">
                  <div className="flex justify-between">
                    <span>Country:</span>
                    <span className="text-gray-900 dark:text-white font-medium">{country.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Photo Size:</span>
                    <span className="text-gray-900 dark:text-white">{country.photo_width} × {country.photo_height} pixels</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Print Size:</span>
                    <span className="text-gray-900 dark:text-white">{(country.photo_width * 0.0264583).toFixed(1)} × {(country.photo_height * 0.0264583).toFixed(1)} cm</span>
                  </div>
                </div>
              </div>

              {/* Processing Info */}
              <div className="bg-green-800 dark:bg-green-900 border border-green-600 dark:border-green-700 p-4 rounded-lg">
                <h3 className="font-semibold text-green-100 mb-2 flex items-center">
                  <Icon icon={ICONS.LOCK} className="mr-2" />
                  Privacy Protected
                </h3>
                <div className="text-sm text-green-200">
                  All processing happens on your device. Your photos never leave your computer.
                </div>
              </div>

            </div>
          </div>

      </div>
    </div>
  );
}));

ManualSelectionEditor.displayName = 'ManualSelectionEditor';

export default ManualSelectionEditor;
